import { useEffect } from "react";

type Options = {
  chatSelector?: string;                // your chat container
  excludeSelectors?: string[];          // things that should keep native scroll
};

export function usePropagateScroll({
  chatSelector = ".chat-container",
  excludeSelectors = [".settings", ".settings-panel", "[data-native-scroll]"],
}: Options = {}) {
  useEffect(() => {
    let chat: HTMLElement | null = null;
    let rafId = 0;

    const isExcluded = (target: Node | null) => {
      if (!(target instanceof Element)) return false;
      return excludeSelectors.some((sel) => target.closest(sel));
    };

    const canAncestorScroll = (start: Node | null, deltaY: number) => {
      let el: Element | null =
        start instanceof Element ? start : (start as Node)?.parentElement;

      while (el && el !== document.documentElement) {
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;

        const scrollableY =
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          el.scrollHeight > el.clientHeight;

        if (scrollableY) {
          const top = el.scrollTop;
          const maxTop = el.scrollHeight - el.clientHeight;
          const scrollingUpPossible = deltaY < 0 && top > 0;
          const scrollingDownPossible = deltaY > 0 && top < maxTop;

          if (scrollingUpPossible || scrollingDownPossible) {
            return true; // this ancestor can consume the wheel
          }
        }

        el = el.parentElement;
      }

      return false;
    };

    const attach = () => {
      chat = document.querySelector(chatSelector);
      if (!chat) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      const handleWheel = (e: WheelEvent) => {
        if (!chat) return;

        const target = e.target as Node | null;

        // 1) Native scroll inside chat
        if (target && chat.contains(target)) return;

        // 2) Skip interception for excluded areas (e.g., settings)
        if (isExcluded(target)) return;

        // 3) If any ancestor of the target can actually scroll in this direction, let it
        if (canAncestorScroll(target, e.deltaY)) return;

        // 4) Dead area → forward to chat and prevent body scroll
        chat.scrollBy({ top: e.deltaY, behavior: "auto" });
        e.preventDefault();
      };

      // Must be passive:false to be able to call preventDefault
      document.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        document.removeEventListener("wheel", handleWheel as EventListener);
      };
    };

    const cleanup = attach();

    return () => {
      cleanup && cleanup();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [chatSelector, excludeSelectors]);
}
